import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, CheckCircle, Circle, Calendar, User, Clock, AlertCircle, X, Save } from 'lucide-react';
import { leadTasksAPI } from '../services/api';

// Task Status Badge Component
const StatusBadge = ({ status }) => {
  const colors = {
    'Pending': 'bg-amber-100 text-amber-700 border-amber-200',
    'In Progress': 'bg-blue-100 text-blue-700 border-blue-200',
    'Completed': 'bg-green-100 text-green-700 border-green-200',
    'Overdue': 'bg-red-100 text-red-700 border-red-200'
  };
  
  return (
    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${colors[status] || colors['Pending']}`}>
      {status}
    </span>
  );
};

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const colors = {
    'Low': 'bg-gray-100 text-gray-600',
    'Medium': 'bg-amber-100 text-amber-700',
    'High': 'bg-red-100 text-red-700'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[priority] || colors['Medium']}`}>
      {priority}
    </span>
  );
};

// Add/Edit Task Modal
const TaskModal = ({ isOpen, onClose, onSave, task, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: new Date().toISOString().split('T')[0],
    priority: 'Medium',
    status: 'Pending',
    assigned_to: ''
  });
  
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        priority: task.priority || 'Medium',
        status: task.status || 'Pending',
        assigned_to: task.assigned_to || ''
      });
    } else {
      setFormData({
        title: '',
        description: '',
        due_date: new Date().toISOString().split('T')[0],
        priority: 'Medium',
        status: 'Pending',
        assigned_to: ''
      });
    }
  }, [task, isOpen]);
  
  if (!isOpen) return null;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">
            {task ? 'Edit Task' : 'Add New Task'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full"
            data-testid="close-task-modal"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              required
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              placeholder="Enter task title"
              data-testid="task-title-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none resize-none"
              placeholder="Enter task description (optional)"
              data-testid="task-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="task-due-date-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                data-testid="task-priority-select"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                data-testid="task-status-select"
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <input
                type="text"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                placeholder="e.g. John Doe"
                data-testid="task-assigned-to-input"
              />
            </div>
          </div>
          
          <div className="pt-4 flex justify-end space-x-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
              data-testid="cancel-task-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center disabled:opacity-50"
              data-testid="save-task-btn"
            >
              <Save size={18} className="mr-2" />
              {loading ? 'Saving...' : (task ? 'Update Task' : 'Create Task')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, taskTitle, loading }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="text-red-600" size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Task</h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete <span className="font-medium">"{taskTitle}"</span>? 
            This action cannot be undone.
          </p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center disabled:opacity-50"
              data-testid="confirm-delete-btn"
            >
              <Trash2 size={18} className="mr-2" />
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Lead Tasks Tab Component
const LeadTasksTab = ({ leadId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [taskModal, setTaskModal] = useState({ open: false, task: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, task: null });
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (leadId) {
      fetchTasks();
    }
  }, [leadId]);
  
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await leadTasksAPI.getAll(leadId);
      setTasks(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveTask = async (taskData) => {
    try {
      setActionLoading(true);
      if (taskModal.task) {
        // Update existing task
        await leadTasksAPI.update(taskModal.task.id, taskData);
      } else {
        // Create new task
        await leadTasksAPI.create(leadId, taskData);
      }
      setTaskModal({ open: false, task: null });
      fetchTasks();
    } catch (err) {
      console.error('Error saving task:', err);
      alert('Failed to save task. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDeleteTask = async () => {
    if (!deleteModal.task) return;
    
    try {
      setActionLoading(true);
      await leadTasksAPI.delete(deleteModal.task.id);
      setDeleteModal({ open: false, task: null });
      fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleToggleComplete = async (task) => {
    try {
      if (task.status === 'Completed') {
        // Reopen task
        await leadTasksAPI.update(task.id, { status: 'Pending' });
      } else {
        // Mark complete
        await leadTasksAPI.markComplete(task.id);
      }
      fetchTasks();
    } catch (err) {
      console.error('Error updating task:', err);
      alert('Failed to update task. Please try again.');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <AlertCircle className="text-red-500 mx-auto mb-2" size={24} />
        <p className="text-red-700">{error}</p>
        <button 
          onClick={fetchTasks}
          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="lead-tasks-tab">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div>
          <h3 className="font-semibold text-gray-800">Tasks</h3>
          <p className="text-sm text-gray-500">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setTaskModal({ open: true, task: null })}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
          data-testid="add-lead-task-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Task
        </button>
      </div>
      
      {/* Tasks Table */}
      {tasks.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <Clock className="text-gray-300 mx-auto mb-3" size={48} />
          <h4 className="text-lg font-medium text-gray-600 mb-1">No tasks yet</h4>
          <p className="text-gray-500 mb-4">Create your first task to track activities for this lead.</p>
          <button
            onClick={() => setTaskModal({ open: true, task: null })}
            className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            data-testid="add-first-task-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Task
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-10"></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Task</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Assigned To</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tasks.map((task) => (
                  <tr 
                    key={task.id} 
                    className={`hover:bg-gray-50 transition-colors ${task.status === 'Completed' ? 'bg-gray-50/50' : ''}`}
                    data-testid={`task-row-${task.id}`}
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleComplete(task)}
                        className={`p-1 rounded-full transition-colors ${
                          task.status === 'Completed' 
                            ? 'text-green-500 hover:text-green-600' 
                            : 'text-gray-300 hover:text-teal-500'
                        }`}
                        title={task.status === 'Completed' ? 'Mark incomplete' : 'Mark complete'}
                        data-testid={`toggle-task-${task.id}`}
                      >
                        {task.status === 'Completed' ? <CheckCircle size={22} /> : <Circle size={22} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium text-gray-900 ${task.status === 'Completed' ? 'line-through text-gray-500' : ''}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-sm text-gray-500 truncate max-w-xs">{task.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar size={14} className="mr-1.5 text-gray-400" />
                        {formatDate(task.due_date)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <PriorityBadge priority={task.priority} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={task.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <User size={14} className="mr-1.5 text-gray-400" />
                        {task.assigned_to || 'Unassigned'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => setTaskModal({ open: true, task })}
                          className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Edit task"
                          data-testid={`edit-task-${task.id}`}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, task })}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete task"
                          data-testid={`delete-task-${task.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Task Summary */}
      {tasks.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-800">{tasks.length}</p>
            <p className="text-sm text-gray-500">Total Tasks</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">
              {tasks.filter(t => t.status === 'Pending' || t.status === 'In Progress').length}
            </p>
            <p className="text-sm text-gray-500">Pending</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {tasks.filter(t => t.status === 'Completed').length}
            </p>
            <p className="text-sm text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {tasks.filter(t => t.status === 'Overdue').length}
            </p>
            <p className="text-sm text-gray-500">Overdue</p>
          </div>
        </div>
      )}
      
      {/* Modals */}
      <TaskModal
        isOpen={taskModal.open}
        onClose={() => setTaskModal({ open: false, task: null })}
        onSave={handleSaveTask}
        task={taskModal.task}
        loading={actionLoading}
      />
      
      <DeleteModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, task: null })}
        onConfirm={handleDeleteTask}
        taskTitle={deleteModal.task?.title}
        loading={actionLoading}
      />
    </div>
  );
};

export default LeadTasksTab;
